import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldCheck, KeyRound, Loader2, Eye, EyeOff, AlertCircle, Truck, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { useAdminAuth, API } from '../context/AdminAuthContext';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const AdminCredentials = () => {
  const { email, authHeaders, logout } = useAdminAuth();
  const { toast } = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  // RBAC: the Driver/Customer credential quick-links are super-admin
  // only — a sub-admin can still land here to change their own password
  // but should not see the cross-role shortcuts.
  const [isSuper, setIsSuper] = useState(false);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/admin/me`, { headers: authHeaders })
      .then(({ data }) => {
        if (alive) setIsSuper(!!data?.is_super_admin);
      })
      .catch(() => {
        if (alive) setIsSuper(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast({ title: 'Password too short', description: 'Use at least 8 characters.' });
      return;
    }
    if (next !== confirm) {
      toast({ title: "Passwords don't match" });
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/change-password`,
        { current_password: current, new_password: next },
        { headers: authHeaders },
      );
      toast({
        title: 'Password updated ✓',
        description: 'You will be signed out — please sign in with your new password.',
      });
      setCurrent('');
      setNext('');
      setConfirm('');
      // Force re-login with the new credentials after a brief pause
      setTimeout(() => {
        logout();
        window.location.href = '/admin/login';
      }, 1800);
    } catch (err) {
      toast({
        title: 'Could not change password',
        description: err?.response?.data?.detail || 'Try again',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-6 lg:p-10 max-w-3xl">
        <div className="mb-8">
          <h1 className="text-3xl font-extrabold text-[#0F172A] font-display">Credentials</h1>
          <p className="text-neutral-500 mt-1">
            Manage your own admin password and review where to control credentials for other roles.
          </p>
        </div>

        {/* Admin own password */}
        <section className="bg-white rounded-2xl border border-neutral-200 p-6 mb-6" data-testid="admin-change-pwd-card">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-xl bg-[#0078D4]/10 ring-1 ring-[#0078D4]/30 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-[#0078D4]" />
            </div>
            <div>
              <h2 className="font-display font-bold text-lg text-[#0F172A]">Change my password</h2>
              <p className="text-xs text-neutral-500 mt-0.5">
                Signed in as <span className="font-mono">{email}</span>
              </p>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <Field label="Current password" required>
              <PasswordInput
                value={current}
                onChange={setCurrent}
                show={show}
                setShow={setShow}
                testid="admin-pwd-current"
              />
            </Field>
            <Field label="New password (≥ 8 characters)" required>
              <PasswordInput value={next} onChange={setNext} show={show} setShow={setShow} testid="admin-pwd-new" />
            </Field>
            <Field label="Confirm new password" required>
              <PasswordInput
                value={confirm}
                onChange={setConfirm}
                show={show}
                setShow={setShow}
                testid="admin-pwd-confirm"
              />
            </Field>

            <div className="flex items-center gap-2 rounded-xl bg-amber-50 border border-amber-100 px-4 py-3 text-xs text-amber-800">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              You'll be signed out after a successful change. Sign back in with the new password.
            </div>

            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={busy}
                className="rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white font-semibold"
                data-testid="admin-pwd-submit"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <KeyRound className="w-4 h-4 mr-1" />}
                Update password
              </Button>
            </div>
          </form>
        </section>

        {/* Quick links to other credential surfaces — super-admin only */}
        {isSuper && (
        <section className="grid sm:grid-cols-2 gap-4">
          <Link
            to="/admin/drivers"
            className="group bg-white rounded-2xl border border-neutral-200 hover:border-emerald-300 hover:shadow-md transition p-5"
            data-testid="link-driver-credentials"
          >
            <div className="w-10 h-10 rounded-xl bg-emerald-100 ring-1 ring-emerald-200 flex items-center justify-center mb-3">
              <Truck className="w-5 h-5 text-emerald-700" />
            </div>
            <h3 className="font-display font-bold text-[#0F172A]">Driver credentials</h3>
            <p className="text-xs text-neutral-500 mt-1 leading-relaxed">
              Reset driver passwords, approve / reject signups, enable / disable accounts.
            </p>
            <span className="text-xs font-semibold text-emerald-700 mt-3 inline-block group-hover:underline">
              Open Drivers →
            </span>
          </Link>

          <Link
            to="/admin/customers"
            className="group bg-white rounded-2xl border border-neutral-200 hover:border-purple-300 hover:shadow-md transition p-5"
            data-testid="link-customer-credentials"
          >
            <div className="w-10 h-10 rounded-xl bg-purple-100 ring-1 ring-purple-200 flex items-center justify-center mb-3">
              <Users className="w-5 h-5 text-purple-700" />
            </div>
            <h3 className="font-display font-bold text-[#0F172A]">Customer credentials</h3>
            <p className="text-xs text-neutral-500 mt-1 leading-relaxed">
              View / revoke passkeys, lock or unlock customer accounts.
            </p>
            <span className="text-xs font-semibold text-purple-700 mt-3 inline-block group-hover:underline">
              Open Customers →
            </span>
          </Link>
        </section>
        )}
      </div>
    </AdminLayout>
  );
};

const Field = ({ label, children, required }) => (
  <div>
    <label className="text-[11px] font-bold text-neutral-700 uppercase tracking-wider">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const PasswordInput = ({ value, onChange, show, setShow, testid }) => (
  <div className="relative">
    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
    <Input
      type={show ? 'text' : 'password'}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required
      className="h-11 pl-10 pr-10 rounded-xl"
      data-testid={testid}
    />
    <button
      type="button"
      onClick={() => setShow((s) => !s)}
      className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-[#0078D4]"
      aria-label={show ? 'Hide password' : 'Show password'}
    >
      {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
    </button>
  </div>
);

export default AdminCredentials;
